import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Header from "./Header";
import { auth } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import {
  getExplorerTracks,
  likeTrack,
  isTrackLiked,
  toggleFavorite,
  isTrackFavorited,
  incrementTrackPlays,
  getLeaderboardByMetric,
  getTopTracksByMetric
} from "./database";

function Explorer() {
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState("explore");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("recent");
  const [timeRange, setTimeRange] = useState("all");
  const [leaderboardPeriod, setLeaderboardPeriod] = useState("weekly");
  const [leaderboardSortBy, setLeaderboardSortBy] = useState("plays");
  const [leaderboardSubTab, setLeaderboardSubTab] = useState("users");
  const [tracks, setTracks] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [topTracks, setTopTracks] = useState([]);
  const [likedTracksMap, setLikedTracksMap] = useState({});
  const [favoritedTracksMap, setFavoritedTracksMap] = useState({});
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [loading, setLoading] = useState(true);


  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    loadExplorerData();
  }, [sortBy, timeRange]);

  useEffect(() => {
    if (user && tracks.length > 0) {
      loadUserInteractions(tracks);
    }
  }, [user, tracks.length]);

  useEffect(() => {
    if (activeTab === "leaderboard") {
      if (leaderboardSubTab === "users") {
        loadLeaderboard();
      } else {
        loadTopTracks();
      }
    }
  }, [activeTab, leaderboardPeriod, leaderboardSortBy, leaderboardSubTab]);


  const loadExplorerData = async () => {
    setLoading(true);
    try {
      let filterType = "newest";

      if (sortBy === "recent") filterType = "newest";
      else if (sortBy === "popular") filterType = "mostLiked";
      else if (sortBy === "trending") filterType = "mostPlayed";

      const fetchedTracks = await getExplorerTracks(filterType);
      setTracks(fetchedTracks);
      if (user && fetchedTracks.length > 0) {
        loadUserInteractions(fetchedTracks);
      }
      console.log("✅ Explorer tracks loaded:", fetchedTracks.length);
    } catch (error) {
      console.error("❌ Explorer loading error:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadLeaderboard = async () => {
    setLoading(true);
    try {
      const data = await getLeaderboardByMetric(leaderboardPeriod, leaderboardSortBy);
      setLeaderboard(data);
      console.log("✅ Leaderboard loaded:", data.length);
    } catch (error) {
      console.error("❌ Leaderboard loading error:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadTopTracks = async () => {
    setLoading(true);
    try {
      const data = await getTopTracksByMetric(leaderboardPeriod, leaderboardSortBy);
      setTopTracks(data);
      if (user) {
        loadUserInteractionsForTopTracks(data);
      }
      console.log("✅ Top Tracks loaded:", data.length);
    } catch (error) {
      console.error("❌ Top Tracks loading error:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserInteractions = async (trackList = tracks) => {
    if (!user || !trackList || trackList.length === 0) return;

    // Yükleniyor durumunu engellemek için console.log açabilirsin
    // console.log("Beğeniler yükleniyor...");

    const newLikes = {};
    const newFavorites = {};

    await Promise.all(trackList.map(async (track) => {
      try {
        const liked = await isTrackLiked(track.id, user.uid);
        const favorited = await isTrackFavorited(track.id, user.uid);

        newLikes[track.id] = liked;
        newFavorites[track.id] = favorited;
      } catch (error) {
        console.error("Interaction check failed:", error);
      }
    }));

    // Önceki state'i koruyarak yenilerini ekle (Spread operator kullanımı doğruydu ama garanti olsun)
    setLikedTracksMap(prev => ({ ...prev, ...newLikes }));
    setFavoritedTracksMap(prev => ({ ...prev, ...newFavorites }));
  };

  const loadUserInteractionsForTopTracks = async (trackList = topTracks) => {
    if (!user || trackList.length === 0) return;

    const likesMap = { ...likedTracksMap };
    const favoritesMap = { ...favoritedTracksMap };

    await Promise.all(trackList.map(async (track) => {
      if (favoritesMap[track.id] === undefined) {
        const liked = await isTrackLiked(track.id, user.uid);
        const favorited = await isTrackFavorited(track.id, user.uid);
        likesMap[track.id] = liked;
        favoritesMap[track.id] = favorited;
      }
    }));

    setLikedTracksMap(likesMap);
    setFavoritedTracksMap(favoritesMap);
  };

  const getTimeRangeDate = () => {
    const now = new Date();
    const date = new Date();

    if (timeRange === "week") {
      date.setDate(now.getDate() - 7);
    } else if (timeRange === "month") {
      date.setMonth(now.getMonth() - 1);
    } else if (timeRange === "year") {
      date.setFullYear(now.getFullYear() - 1);
    } else if (timeRange === "all") {
      return new Date(0);
    }

    return date;
  };

  const filteredTracks = tracks.filter(track => {
    const matchesSearch = track.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      track.ownerUsername.toLowerCase().includes(searchQuery.toLowerCase());

    const trackDate = track.createdAt?.toDate ? track.createdAt.toDate() : new Date(0);
    const rangeDate = getTimeRangeDate();
    const matchesTimeRange = trackDate >= rangeDate;

    return matchesSearch && matchesTimeRange;
  });

  const handleLike = async (trackId) => {
    if (!user) {
      alert("❌ You must be logged in to like!");
      return;
    }

    // 1. Önce DB'den DEĞİL, elindeki mevcut map'ten durumu al
    const currentlyLiked = likedTracksMap[trackId] || false;
    const targetStatus = !currentlyLiked; // Hedeflenen yeni durum

    // 2. Optimistic UI Update (Arayüzü hemen güncelle)
    setLikedTracksMap(prev => ({
      ...prev,
      [trackId]: targetStatus
    }));

    // Sayaçları güncelle
    const updateCounts = (list) => list.map(track => {
      if (track.id === trackId) {
        return {
          ...track,
          likes: targetStatus ? (track.likes || 0) + 1 : Math.max(0, (track.likes || 0) - 1)
        };
      }
      return track;
    });

    setTracks(prev => updateCounts(prev));
    setTopTracks(prev => updateCounts(prev));

    // 3. DB İşlemini Arka Planda Yap
    try {
      const result = await likeTrack(trackId, user.uid);

      // Eğer DB'den dönen sonuç, bizim tahminimizden farklıysa düzelt
      if (result !== null && result !== targetStatus) {
        setLikedTracksMap(prev => ({
          ...prev,
          [trackId]: result
        }));
        // Sayacı geri al (Rollback logic)
        // Burası çok nadir olur, istersen loadExplorerData() çağırabilirsin.
      }
    } catch (error) {
      console.error("❌ Beğeni hatası:", error);
      // Hata olursa eski haline döndür
      setLikedTracksMap(prev => ({
        ...prev,
        [trackId]: currentlyLiked // Eski duruma dön
      }));
      // Sayaçları da eski haline döndürmek gerekir...
    }
  };

  const handleFavorite = async (track, e) => {
    if (e && e.stopPropagation) e.stopPropagation();

    if (!user) {
      alert("❌ You must be logged in to add to favorites!");
      return;
    }

    // 1. Mevcut durumu map'ten al
    const isCurrentlyFavorited = favoritedTracksMap[track.id] || false;
    const targetStatus = !isCurrentlyFavorited;

    // 2. UI Güncelle
    setFavoritedTracksMap(prev => ({
      ...prev,
      [track.id]: targetStatus
    }));

    const updateCounts = (list) => list.map(t => {
      if (t.id === track.id) {
        const currentCount = t.favorites || 0;
        const newCount = targetStatus ? currentCount + 1 : Math.max(0, currentCount - 1);
        return { ...t, favorites: newCount };
      }
      return t;
    });

    setTracks(prev => updateCounts(prev));
    setTopTracks(prev => updateCounts(prev));

    // 3. DB İşlemi
    try {
      await toggleFavorite(track.id, user.uid);
      // Başarılı olursa başka bir şey yapmana gerek yok, UI zaten güncel.
    } catch (error) {
      console.error("❌ Favori hatası:", error);
      // Hata durumunda UI'ı eski haline döndür
      setFavoritedTracksMap(prev => ({
        ...prev,
        [track.id]: isCurrentlyFavorited
      }));
      // Gerekirse loadExplorerData() ile veriyi tazeleyebilirsiniz.
    }
  };

  const handlePlay = async (track) => {
    setSelectedTrack(track);
    setShowCodeModal(true);
  };

  const handleTestCode = async () => {
    if (!selectedTrack) return;

    if (user) {
      incrementTrackPlays(selectedTrack.id, user.uid).then(success => {
        if (success) console.log("✅ Play count incremented via Studio launch");
      });
    }

    window.location.href = '/studio';

    localStorage.setItem('loadedTrackFromExplorer', JSON.stringify({
      code: selectedTrack.code,
      title: selectedTrack.title,
      trackId: selectedTrack.id,
      isPublic: true, // Explorer'dan gelen her track public'tir
      ownerUsername: selectedTrack.ownerUsername
    }));
  };

  return (
    <div style={styles.pageContainer}>
      <Header user={user} sticky={false} />

      {showCodeModal && selectedTrack && (
        <div style={styles.modalOverlay} onClick={() => setShowCodeModal(false)}>
          <div style={styles.codeModal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <h2 style={styles.modalTitle}>{selectedTrack.title}</h2>
                <p style={styles.modalSubtitle}>by @{selectedTrack.ownerUsername}</p>
              </div>
              <button
                style={styles.closeButton}
                onClick={() => setShowCodeModal(false)}
              >
                ✕
              </button>
            </div>

            <div style={styles.codeEditorContainer}>
              <textarea
                value={selectedTrack.code}
                readOnly
                style={styles.codeEditor}
                spellCheck={false}
              />
            </div>

            <div style={styles.modalFooter}>
              <div style={styles.modalStats}>
                <span style={styles.modalStat}>
                  ▶ {selectedTrack.plays || 0} plays
                </span>
                <span style={styles.modalStat}>
                  ♥ {selectedTrack.likes || 0} likes
                </span>
              </div>
              <button
                style={styles.testButton}
                onClick={handleTestCode}
              >
                ▶ Play in Studio
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={styles.subHeader}>
        <div style={styles.subHeaderContent}>
          {/* <h1 style={styles.title}>
            {activeTab === "explore" ? "EXPLORE COMPOSITIONS" : "TOP RANKS"}
          </h1> */}

          <div style={styles.tabSwitcher}>
            <button
              onClick={() => setActiveTab("explore")}
              style={{
                ...styles.tabSwitcherBtn,
                ...(activeTab === "explore" ? styles.tabSwitcherBtnActive : {})
              }}
            >
              Explore
            </button>
            <button
              onClick={() => setActiveTab("leaderboard")}
              style={{
                ...styles.tabSwitcherBtn,
                ...(activeTab === "leaderboard" ? styles.tabSwitcherBtnActive : {})
              }}
            >
              Top Ranks
            </button>
          </div>
        </div>
      </div>

      {activeTab === "explore" && (
        <div style={styles.searchSection}>
          <div style={styles.searchBar}>
            <span style={styles.searchIcon}>🔍</span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search compositions or artists..."
              style={styles.searchInput}
              className="explorer-search-input"
            />
            {searchQuery && (
              <button
                style={styles.clearButton}
                onClick={() => setSearchQuery("")}
              >
                ✕
              </button>
            )}
          </div>

          <div style={styles.filterBar}>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>SORT BY</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={styles.select}
              >
                <option value="recent">Most Recent</option>
                <option value="popular">Most Liked</option>
                <option value="trending">Most Played</option>
              </select>
            </div>

            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>TIME RANGE</label>
              <div style={styles.timeRangeButtons}>
                <button
                  onClick={() => setTimeRange("week")}
                  style={{
                    ...styles.timeRangeBtn,
                    ...(timeRange === "week" ? styles.timeRangeBtnActive : {})
                  }}
                >
                  📅 Week
                </button>
                <button
                  onClick={() => setTimeRange("month")}
                  style={{
                    ...styles.timeRangeBtn,
                    ...(timeRange === "month" ? styles.timeRangeBtnActive : {})
                  }}
                >
                  📆 Month
                </button>
                <button
                  onClick={() => setTimeRange("year")}
                  style={{
                    ...styles.timeRangeBtn,
                    ...(timeRange === "year" ? styles.timeRangeBtnActive : {})
                  }}
                >
                  📊 Year
                </button>
                <button
                  onClick={() => setTimeRange("all")}
                  style={{
                    ...styles.timeRangeBtn,
                    ...(timeRange === "all" ? styles.timeRangeBtnActive : {})
                  }}
                >
                  ∞ All Time
                </button>
              </div>
            </div>

            <div style={styles.resultsCount}>
              {filteredTracks.length} composition{filteredTracks.length !== 1 ? 's' : ''} found
            </div>
          </div>
        </div>
      )}

      {activeTab === "leaderboard" && (
        <div style={styles.searchSection}>
          <div style={styles.leaderboardSubTabs}>
            <button
              onClick={() => setLeaderboardSubTab("users")}
              style={{
                ...styles.subTabBtn,
                ...(leaderboardSubTab === "users" ? styles.subTabBtnActive : {})
              }}
            >
              Top Artists
            </button>
            <button
              onClick={() => setLeaderboardSubTab("tracks")}
              style={{
                ...styles.subTabBtn,
                ...(leaderboardSubTab === "tracks" ? styles.subTabBtnActive : {})
              }}
            >
              Top Tracks
            </button>
          </div>

          <div style={styles.filterBar}>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>SORT BY</label>
              <select
                value={leaderboardSortBy}
                onChange={(e) => setLeaderboardSortBy(e.target.value)}
                style={styles.select}
              >
                <option value="plays">Most Played</option>
                <option value="likes">Most Liked</option>
                <option value="favorites">Most Favorited</option>
              </select>
            </div>

            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>TIME PERIOD</label>
              <select
                value={leaderboardPeriod}
                onChange={(e) => setLeaderboardPeriod(e.target.value)}
                style={styles.select}
              >
                <option value="weekly">Last 7 Days</option>
                <option value="monthly">Last 30 Days</option>
                <option value="yearly">Last 365 Days</option>
                <option value="all_time">All Time</option>
              </select>
            </div>

            <div style={styles.resultsCount}>
              {leaderboardSubTab === "users"
                ? `Top ${leaderboard.length} artists`
                : `Top ${topTracks.length} tracks`
              }
            </div>
          </div>
        </div>
      )}

      <div style={styles.listContainer}>
        {loading ? (
          <div style={styles.loadingState}>
            <div style={styles.spinner}></div>
            <p>Loading...</p>
          </div>
        ) : activeTab === "explore" ? (
          filteredTracks.length === 0 ? (
            <div style={styles.emptyState}>
              <p style={styles.emptyIcon}>🎵</p>
              <p style={styles.emptyText}>No compositions found</p>
              <p style={styles.emptySubtext}>
                {searchQuery ? "Try adjusting your search or filters" : "Be the first to create and share!"}
              </p>
            </div>
          ) : (
            filteredTracks.map((track) => {
              const isLiked = likedTracksMap[track.id] || false;
              const isFavorited = favoritedTracksMap[track.id] || false;

              return (
                <motion.div
                  key={track.id}
                  style={styles.trackItem}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: filteredTracks.indexOf(track) * 0.05 }}
                  whileHover={{ scale: 1.02, boxShadow: "0 10px 40px rgba(0, 216, 255, 0.15)" }}
                  onClick={() => handlePlay(track)}
                >
                  <button
                    style={styles.playBtn}
                    onClick={(e) => { e.stopPropagation(); handlePlay(track); }}
                    title="View Code"
                  >
                    ▶
                  </button>

                  <div style={styles.trackInfo}>
                    <div style={styles.trackHeader}>
                      <h3 style={styles.trackTitle}>{track.title}</h3>
                      <div style={styles.trackMeta}>
                        <span style={styles.metaItem}>
                          👤 {track.ownerUsername}
                        </span>
                        <span style={styles.metaDot}>•</span>
                        <span style={styles.metaItem}>
                          📅 {track.createdAt?.toDate ?
                            track.createdAt.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                            : 'Unknown date'}
                        </span>
                        <span style={styles.metaDot}>•</span>
                        <span style={styles.metaItem}>
                          {track.code ? track.code.split('\n').length : 0} lines
                        </span>
                      </div>
                    </div>
                  </div>

                  <div style={styles.trackStats}>
                    <div style={styles.statItem}>
                      <span style={styles.statIcon}>▶</span>
                      <span style={styles.statValue}>{track.plays || 0}</span>
                    </div>
                    <div style={styles.statItem}>
                      <span style={styles.statIcon}>♥</span>
                      <span style={styles.statValue}>{track.likes || 0}</span>
                    </div>
                    <div style={styles.statItem}>
                      <span style={styles.statIcon}>⭐</span>
                      <span style={styles.statValue}>{track.favorites || 0}</span>
                    </div>
                  </div>

                  <button
                    onClick={(e) => { e.stopPropagation(); handleFavorite(track, e); }}
                    style={{
                      ...styles.favoriteBtn,
                      ...(isFavorited ? styles.favoriteBtnActive : {})
                    }}
                    title={isFavorited ? "Remove from favorites" : "Add to favorites"}
                  >
                    {isFavorited ? "⭐" : "☆"}
                  </button>

                  <button
                    onClick={(e) => { e.stopPropagation(); handleLike(track.id); }}
                    style={{
                      ...styles.likeBtn,
                      ...(isLiked ? styles.likeBtnActive : {})
                    }}
                    title={isLiked ? "Unlike" : "Like"}
                  >
                    {isLiked ? "♥" : "♡"}
                  </button>
                </motion.div>
              );
            })
          )
        ) : (
          <>
            {leaderboardSubTab === "users" ? (
              <>
                {leaderboard.length === 0 ? (
                  <div style={styles.emptyState}>
                    <p style={styles.emptyIcon}>👥</p>
                    <p style={styles.emptyText}>No artists found</p>
                    <p style={styles.emptySubtext}>
                      No artists available for this time period
                    </p>
                  </div>
                ) : (
                  <>
                    {leaderboard.length >= 3 && (
                      <div style={styles.podiumContainer}>
                        <div style={{ ...styles.podiumCard, ...styles.podiumSecond }}>
                          <div style={styles.podiumRank}>
                            <span style={styles.podiumMedal}>🥈</span>
                            <span style={styles.podiumNumber}>#2</span>
                          </div>
                          <div style={styles.podiumAvatar}>
                            {leaderboard[1].username.charAt(0).toUpperCase()}
                          </div>
                          <h3 style={styles.podiumUsername}>@{leaderboard[1].username}</h3>
                          <div style={styles.podiumStats}>
                            <div style={styles.podiumStat}>
                              <span style={styles.podiumStatLabel}>
                                {leaderboardSortBy === "plays" ? "Plays" : leaderboardSortBy === "likes" ? "Likes" : "Favorites"}
                              </span>
                              <span style={styles.podiumStatValue}>
                                {leaderboardSortBy === "plays"
                                  ? leaderboard[1].totalPlays?.toLocaleString() || 0
                                  : leaderboardSortBy === "likes"
                                    ? leaderboard[1].totalLikes?.toLocaleString() || 0
                                    : leaderboard[1].totalFavorites?.toLocaleString() || 0
                                }
                              </span>
                            </div>
                            <div style={styles.podiumStat}>
                              <span style={styles.podiumStatLabel}>Tracks</span>
                              <span style={styles.podiumStatValue}>{leaderboard[1].trackCount || 0}</span>
                            </div>
                          </div>
                        </div>

                        <div style={{ ...styles.podiumCard, ...styles.podiumFirst }}>
                          <div style={styles.crownIcon}>👑</div>
                          <div style={styles.podiumRank}>
                            <span style={styles.podiumMedal}>🥇</span>
                            <span style={styles.podiumNumber}>#1</span>
                          </div>
                          <div style={{ ...styles.podiumAvatar, ...styles.podiumAvatarFirst }}>
                            {leaderboard[0].username.charAt(0).toUpperCase()}
                          </div>
                          <h3 style={styles.podiumUsername}>@{leaderboard[0].username}</h3>
                          <div style={styles.podiumStats}>
                            <div style={styles.podiumStat}>
                              <span style={styles.podiumStatLabel}>
                                {leaderboardSortBy === "plays" ? "Plays" : leaderboardSortBy === "likes" ? "Likes" : "Favorites"}
                              </span>
                              <span style={styles.podiumStatValue}>
                                {leaderboardSortBy === "plays"
                                  ? leaderboard[0].totalPlays?.toLocaleString() || 0
                                  : leaderboardSortBy === "likes"
                                    ? leaderboard[0].totalLikes?.toLocaleString() || 0
                                    : leaderboard[0].totalFavorites?.toLocaleString() || 0
                                }
                              </span>
                            </div>
                            <div style={styles.podiumStat}>
                              <span style={styles.podiumStatLabel}>Tracks</span>
                              <span style={styles.podiumStatValue}>{leaderboard[0].trackCount || 0}</span>
                            </div>
                          </div>
                        </div>

                        <div style={{ ...styles.podiumCard, ...styles.podiumThird }}>
                          <div style={styles.podiumRank}>
                            <span style={styles.podiumMedal}>🥉</span>
                            <span style={styles.podiumNumber}>#3</span>
                          </div>
                          <div style={styles.podiumAvatar}>
                            {leaderboard[2].username.charAt(0).toUpperCase()}
                          </div>
                          <h3 style={styles.podiumUsername}>@{leaderboard[2].username}</h3>
                          <div style={styles.podiumStats}>
                            <div style={styles.podiumStat}>
                              <span style={styles.podiumStatLabel}>
                                {leaderboardSortBy === "plays" ? "Plays" : leaderboardSortBy === "likes" ? "Likes" : "Favorites"}
                              </span>
                              <span style={styles.podiumStatValue}>
                                {leaderboardSortBy === "plays"
                                  ? leaderboard[2].totalPlays?.toLocaleString() || 0
                                  : leaderboardSortBy === "likes"
                                    ? leaderboard[2].totalLikes?.toLocaleString() || 0
                                    : leaderboard[2].totalFavorites?.toLocaleString() || 0
                                }
                              </span>
                            </div>
                            <div style={styles.podiumStat}>
                              <span style={styles.podiumStatLabel}>Tracks</span>
                              <span style={styles.podiumStatValue}>{leaderboard[2].trackCount || 0}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div style={styles.leaderboardList}>
                      {(leaderboard.length < 3 ? leaderboard : leaderboard.slice(3)).map((user, index) => {
                        const rank = leaderboard.length < 3 ? index + 1 : index + 4;

                        return (
                          <div key={user.uid} style={styles.leaderboardItem}>
                            <div style={styles.leaderboardRank}>
                              #{rank}
                            </div>

                            <div style={styles.leaderboardAvatar}>
                              {user.username.charAt(0).toUpperCase()}
                            </div>

                            <div style={styles.leaderboardInfo}>
                              <h3 style={styles.leaderboardUsername}>@{user.username}</h3>
                              <div style={styles.leaderboardMeta}>
                                Level {user.level} • {user.trackCount} tracks
                              </div>
                            </div>

                            <div style={styles.leaderboardStats}>
                              <div style={styles.leaderboardStatItem}>
                                <span style={styles.leaderboardStatIcon}>
                                  {leaderboardSortBy === "plays" ? "▶" : leaderboardSortBy === "likes" ? "♥" : "⭐"}
                                </span>
                                <span style={styles.leaderboardStatValue}>
                                  {leaderboardSortBy === "plays"
                                    ? user.totalPlays?.toLocaleString() || 0
                                    : leaderboardSortBy === "likes"
                                      ? user.totalLikes?.toLocaleString() || 0
                                      : user.totalFavorites?.toLocaleString() || 0
                                  }
                                </span>
                              </div>
                              <div style={styles.leaderboardStatItem}>
                                <span style={styles.leaderboardStatIcon}>🎵</span>
                                <span style={styles.leaderboardStatValue}>{user.trackCount || 0}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </>
            ) : (
              <div style={styles.topTracksList}>
                {topTracks.length === 0 ? (
                  <div style={styles.emptyState}>
                    <p style={styles.emptyIcon}>🎵</p>
                    <p style={styles.emptyText}>No tracks found</p>
                    <p style={styles.emptySubtext}>
                      No tracks available for this time period
                    </p>
                  </div>
                ) : (
                  topTracks.map((track, index) => {
                    const rank = index + 1;
                    const isLiked = likedTracksMap[track.id] || false;
                    const isFavorited = favoritedTracksMap[track.id] || false;

                    return (
                      <div key={track.id} style={styles.trackItem}>
                        <div style={styles.trackRank}>
                          #{rank}
                        </div>

                        <button
                          style={styles.playBtn}
                          onClick={() => handlePlay(track)}
                          title="View Code"
                        >
                          ▶
                        </button>

                        <div style={styles.trackInfo}>
                          <div style={styles.trackHeader}>
                            <h3 style={styles.trackTitle}>{track.title}</h3>
                            <div style={styles.trackMeta}>
                              <span style={styles.metaItem}>
                                👤 {track.ownerUsername}
                              </span>
                              <span style={styles.metaDot}>•</span>
                              <span style={styles.metaItem}>
                                📅 {track.createdAt?.toDate ?
                                  track.createdAt.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                  : 'Unknown date'}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div style={styles.trackStats}>
                          <div style={styles.statItem}>
                            <span style={styles.statIcon}>▶</span>
                            <span style={styles.statValue}>{track.plays || 0}</span>
                          </div>
                          <div style={styles.statItem}>
                            <span style={styles.statIcon}>♥</span>
                            <span style={styles.statValue}>{track.likes || 0}</span>
                          </div>
                          <div style={styles.statItem}>
                            <span style={styles.statIcon}>⭐</span>
                            <span style={styles.statValue}>{track.favorites || 0}</span>
                          </div>
                        </div>

                        <button
                          onClick={(e) => handleFavorite(track, e)}
                          style={{
                            ...styles.favoriteBtn,
                            ...(isFavorited ? styles.favoriteBtnActive : {})
                          }}
                          title={isFavorited ? "Remove from favorites" : "Add to favorites"}
                        >
                          {isFavorited ? "⭐" : "☆"}
                        </button>

                        <button
                          onClick={() => handleLike(track.id)}
                          style={{
                            ...styles.likeBtn,
                            ...(isLiked ? styles.likeBtnActive : {})
                          }}
                          title={isLiked ? "Unlike" : "Like"}
                        >
                          {isLiked ? "♥" : "♡"}
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  pageContainer: {
    background: "linear-gradient(180deg, #0a0a0a 0%, #0f0a1a 50%, #0a0a0a 100%)",
    minHeight: "100vh",
    color: "#e0e0e0",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    padding: "0",
    position: "relative"
  },

  subHeader: {
    backgroundColor: "#0a0a0a",
    padding: "40px 0 20px 0",
  },
  subHeaderContent: {
    maxWidth: "1400px",
    margin: "0 auto",
    padding: "0 40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "24px",
    flexWrap: "wrap"
  },
  title: {
    margin: 0,
    fontSize: "26px",
    fontWeight: 700,
    color: "#fff",
    letterSpacing: "-0.5px"
  },
  tabSwitcher: {
    display: "flex",
    gap: "8px",
    background: "rgba(15, 15, 25, 0.9)",
    padding: "6px",
    borderRadius: "14px",
    border: "1px solid rgba(0, 216, 255, 0.2)"
  },
  tabSwitcherBtn: {
    padding: "8px 20px",
    background: "rgba(20, 20, 30, 0.8)",
    color: "#888",
    border: "none",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s",
    fontFamily: "inherit"
  },
  tabSwitcherBtnActive: {
    background: "linear-gradient(135deg, #00d8ff 0%, #c084fc 100%)",
    color: "#fff"
  },

  searchSection: {
    backgroundColor: "rgba(10, 10, 10, 0.95)",
    borderBottom: "1px solid #1f1f1f",
    borderTop: "1px solid #1f1f1f",
    padding: "24px 40px",
  },
  searchBar: {
    position: "relative",
    marginBottom: "20px",
    maxWidth: "1400px",
    margin: "0 auto 20px auto"
  },
  searchIcon: {
    position: "absolute",
    left: "16px",
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: "18px",
    color: "#666"
  },
  searchInput: {
    width: "100%",
    padding: "14px 50px 14px 50px",
    backgroundColor: "#1a1a1a",
    border: "1px solid #2a2a2a",
    borderRadius: "8px",
    color: "#e0e0e0",
    fontSize: "15px",
    fontFamily: "inherit",
    outline: "none",
    transition: "all 0.2s",
    boxSizing: "border-box"
  },
  clearButton: {
    position: "absolute",
    right: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    backgroundColor: "#2a2a2a",
    color: "#999",
    border: "none",
    width: "28px",
    height: "28px",
    borderRadius: "50%",
    cursor: "pointer",
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s"
  },
  filterBar: {
    display: "flex",
    gap: "20px",
    alignItems: "center",
    flexWrap: "wrap",
    maxWidth: "1400px",
    margin: "0 auto"
  },
  filterGroup: {
    display: "flex",
    alignItems: "center",
    gap: "10px"
  },
  filterLabel: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#888",
    letterSpacing: "0.5px"
  },
  select: {
    padding: "8px 32px 8px 12px",
    backgroundColor: "#1a1a1a",
    border: "1px solid #2a2a2a",
    borderRadius: "6px",
    color: "#e0e0e0",
    fontSize: "14px",
    fontFamily: "inherit",
    cursor: "pointer",
    outline: "none",
    appearance: "none",
    backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%23999' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E\")",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 10px center",
    minWidth: "150px"
  },
  timeRangeButtons: {
    display: "flex",
    gap: "8px"
  },
  timeRangeBtn: {
    padding: "6px 14px",
    backgroundColor: "#1a1a1a",
    color: "#888",
    border: "1px solid #2a2a2a",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s",
    fontFamily: "inherit"
  },
  timeRangeBtnActive: {
    backgroundColor: "#2a2a2a",
    color: "#fff",
    borderColor: "#3a3a3a"
  },
  resultsCount: {
    marginLeft: "auto",
    fontSize: "13px",
    color: "#666",
    fontWeight: 500
  },

  listContainer: {
    padding: "24px 40px 60px",
    maxWidth: "1400px",
    margin: "0 auto"
  },
  loadingState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "80px 20px",
    color: "#666"
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "3px solid #2a2a2a",
    borderTop: "3px solid #6366f1",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
    marginBottom: "20px"
  },
  trackItem: {
    display: "flex",
    alignItems: "center",
    gap: "20px",
    padding: "20px",
    background: "linear-gradient(135deg, rgba(20, 20, 35, 0.9) 0%, rgba(15, 15, 25, 0.9) 100%)",
    border: "1px solid rgba(0, 216, 255, 0.15)",
    borderRadius: "10px",
    marginBottom: "12px",
    transition: "all 0.2s",
    cursor: "pointer"
  },
  playBtn: {
    width: "48px",
    height: "48px",
    background: "linear-gradient(135deg, #00d8ff 0%, #00b8e0 100%)",
    color: "#000",
    border: "none",
    borderRadius: "50%",
    cursor: "pointer",
    fontWeight: "bold",
    fontSize: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s",
    paddingLeft: "9px",
    flexShrink: 0,
    fontFamily: "Arial, sans-serif"
  },
  trackInfo: {
    flex: 1,
    minWidth: 0
  },
  trackHeader: {
    display: "flex",
    flexDirection: "column",
    gap: "6px"
  },
  trackTitle: {
    margin: 0,
    fontSize: "16px",
    fontWeight: 600,
    color: "#fff",
    letterSpacing: "-0.2px"
  },
  trackMeta: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap"
  },
  metaItem: {
    fontSize: "13px",
    color: "#888"
  },
  metaDot: {
    color: "#444",
    fontSize: "12px"
  },
  trackStats: {
    display: "flex",
    gap: "20px",
    marginRight: "12px"
  },
  statItem: {
    display: "flex",
    alignItems: "center",
    gap: "6px"
  },
  statIcon: {
    fontSize: "14px",
    color: "#666"
  },
  statValue: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#999"
  },
  likeBtn: {
    width: "40px",
    height: "40px",
    backgroundColor: "transparent",
    color: "#666",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "#2a2a2a",
    borderRadius: "50%",
    borderRadius: "50%",
    cursor: "pointer",
    fontSize: "18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s",
    flexShrink: 0
  },
  likeBtnActive: {
    backgroundColor: "rgba(255, 59, 92, 0.1)",
    color: "#ff3b5c",
    borderColor: "#ff3b5c"
  },
  favoriteBtn: {
    width: "40px",
    height: "40px",
    backgroundColor: "transparent",
    color: "#666",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "#2a2a2a",
    borderRadius: "50%",
    borderRadius: "50%",
    cursor: "pointer",
    fontSize: "18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s",
    flexShrink: 0,
    marginRight: "8px"
  },
  favoriteBtnActive: {
    backgroundColor: "rgba(255, 215, 0, 0.1)",
    color: "#ffd700",
    borderColor: "#ffd700"
  },

  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.85)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2000,
    backdropFilter: "blur(8px)",
    padding: "20px"
  },
  codeModal: {
    backgroundColor: "#141414",
    border: "1px solid #2a2a2a",
    borderRadius: "12px",
    width: "100%",
    maxWidth: "900px",
    maxHeight: "85vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.5)"
  },
  modalHeader: {
    padding: "24px",
    borderBottom: "1px solid #1f1f1f",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  modalTitle: {
    margin: "0 0 6px 0",
    fontSize: "20px",
    fontWeight: 600,
    color: "#fff",
    letterSpacing: "-0.3px"
  },
  modalSubtitle: {
    margin: 0,
    fontSize: "14px",
    color: "#888"
  },
  closeButton: {
    width: "36px",
    height: "36px",
    background: "#1f1f1f",
    color: "#999",
    border: "none",
    borderRadius: "50%",
    cursor: "pointer",
    fontSize: "18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s",
    fontFamily: "inherit",
    fontWeight: 400
  },
  codeEditorContainer: {
    flex: 1,
    overflow: "auto",
    padding: "24px",
    backgroundColor: "#0a0a0a"
  },
  codeEditor: {
    width: "100%",
    minHeight: "400px",
    padding: "20px",
    backgroundColor: "#000",
    color: "#e0e0e0",
    border: "1px solid #1f1f1f",
    borderRadius: "8px",
    fontSize: "14px",
    fontFamily: "'JetBrains Mono', 'Fira Code', Consolas, monospace",
    lineHeight: "1.6",
    resize: "vertical",
    outline: "none",
    boxSizing: "border-box"
  },
  modalFooter: {
    padding: "20px 24px",
    borderTop: "1px solid #1f1f1f",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#0f0f0f"
  },
  modalStats: {
    display: "flex",
    gap: "24px"
  },
  modalStat: {
    fontSize: "14px",
    color: "#888",
    fontWeight: 500
  },
  testButton: {
    padding: "10px 24px",
    background: "linear-gradient(135deg, #00d8ff 0%, #c084fc 100%)",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s",
    fontFamily: "inherit"
  },

  emptyState: {
    textAlign: "center",
    padding: "80px 20px",
    color: "#666"
  },
  emptyIcon: {
    fontSize: "64px",
    marginBottom: "20px",
    opacity: 0.4
  },
  emptyText: {
    fontSize: "18px",
    fontWeight: 600,
    marginBottom: "8px",
    color: "#999"
  },
  emptySubtext: {
    fontSize: "14px",
    color: "#666",
    lineHeight: "1.5"
  },

  podiumContainer: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    gap: "24px",
    marginBottom: "40px",
    padding: "40px 20px"
  },
  podiumCard: {
    backgroundColor: "#141414",
    border: "1px solid #2a2a2a",
    borderRadius: "12px",
    padding: "32px 24px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "280px",
    position: "relative",
    transition: "all 0.2s"
  },
  podiumFirst: {
    transform: "scale(1.08)",
    borderColor: "#ffd700",
    backgroundColor: "rgba(255, 215, 0, 0.05)",
    boxShadow: "0 8px 40px rgba(255, 215, 0, 0.3), inset 0 0 20px rgba(255, 215, 0, 0.1)"
  },
  podiumSecond: {
    borderColor: "#c0c0c0",
    backgroundColor: "rgba(192, 192, 192, 0.03)"
  },
  podiumThird: {
    borderColor: "#cd7f32",
    backgroundColor: "rgba(205, 127, 50, 0.03)"
  },
  crownIcon: {
    position: "absolute",
    top: "-20px",
    fontSize: "32px"
  },
  podiumRank: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "16px"
  },
  podiumMedal: {
    fontSize: "28px"
  },
  podiumNumber: {
    fontSize: "16px",
    fontWeight: 700,
    color: "#999"
  },
  podiumAvatar: {
    width: "80px",
    height: "80px",
    borderRadius: "50%",
    backgroundColor: "#2a2a2a",
    border: "3px solid #3a3a3a",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "32px",
    fontWeight: 700,
    color: "#fff",
    marginBottom: "16px"
  },
  podiumAvatarFirst: {
    width: "96px",
    height: "96px",
    fontSize: "40px",
    borderColor: "#ffd700",
    backgroundColor: "rgba(255, 215, 0, 0.1)"
  },
  podiumUsername: {
    margin: "0 0 16px 0",
    fontSize: "18px",
    fontWeight: 600,
    color: "#fff"
  },
  podiumStats: {
    display: "flex",
    gap: "16px",
    width: "100%",
    justifyContent: "space-between"
  },
  podiumStat: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "4px"
  },
  podiumStatLabel: {
    fontSize: "11px",
    color: "#666",
    textTransform: "uppercase",
    fontWeight: 600
  },
  podiumStatValue: {
    fontSize: "14px",
    color: "#ccc",
    fontWeight: 600
  },
  leaderboardList: {
    maxWidth: "800px",
    margin: "0 auto"
  },
  leaderboardItem: {
    display: "flex",
    alignItems: "center",
    gap: "20px",
    padding: "24px 32px",
    background: "linear-gradient(135deg, rgba(20, 20, 35, 0.9) 0%, rgba(15, 15, 25, 0.9) 100%)",
    border: "1px solid rgba(0, 216, 255, 0.15)",
    borderRadius: "16px",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)",
    transition: "all 0.3s ease",
    cursor: "pointer",
    marginBottom: "10px"
  },
  leaderboardRank: {
    fontSize: "18px",
    fontWeight: 700,
    color: "#666",
    width: "40px"
  },
  leaderboardAvatar: {
    width: "48px",
    height: "48px",
    borderRadius: "50%",
    backgroundColor: "#2a2a2a",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "20px",
    fontWeight: 600,
    color: "#fff"
  },
  leaderboardInfo: {
    flex: 1
  },
  leaderboardUsername: {
    margin: "0 0 4px 0",
    fontSize: "16px",
    fontWeight: 600,
    color: "#fff"
  },
  leaderboardMeta: {
    fontSize: "13px",
    color: "#666"
  },
  leaderboardStats: {
    display: "flex",
    gap: "24px"
  },
  leaderboardStatItem: {
    display: "flex",
    alignItems: "center",
    gap: "6px"
  },
  testButton: {
    padding: "10px 24px",
    background: "linear-gradient(135deg, #00d8ff 0%, #c084fc 100%)",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s",
    fontFamily: "inherit"
  },

  emptyState: {
    textAlign: "center",
    padding: "80px 20px",
    color: "#666"
  },
  emptyIcon: {
    fontSize: "64px",
    marginBottom: "20px",
    opacity: 0.4
  },
  emptyText: {
    fontSize: "18px",
    fontWeight: 600,
    marginBottom: "8px",
    color: "#999"
  },
  emptySubtext: {
    fontSize: "14px",
    color: "#666",
    lineHeight: "1.5"
  },

  podiumContainer: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    gap: "24px",
    marginBottom: "40px",
    padding: "40px 20px"
  },
  podiumCard: {
    backgroundColor: "#141414",
    border: "1px solid #2a2a2a",
    borderRadius: "12px",
    padding: "32px 24px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "280px",
    position: "relative",
    transition: "all 0.2s"
  },
  podiumFirst: {
    transform: "scale(1.08)",
    borderColor: "#ffd700",
    backgroundColor: "rgba(255, 215, 0, 0.05)",
    boxShadow: "0 8px 40px rgba(255, 215, 0, 0.3), inset 0 0 20px rgba(255, 215, 0, 0.1)"
  },
  podiumSecond: {
    borderColor: "#c0c0c0",
    backgroundColor: "rgba(192, 192, 192, 0.03)"
  },
  podiumThird: {
    borderColor: "#cd7f32",
    backgroundColor: "rgba(205, 127, 50, 0.03)"
  },
  crownIcon: {
    position: "absolute",
    top: "-20px",
    fontSize: "32px"
  },
  podiumRank: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "16px"
  },
  podiumMedal: {
    fontSize: "28px"
  },
  podiumNumber: {
    fontSize: "16px",
    fontWeight: 700,
    color: "#999"
  },
  podiumAvatar: {
    width: "80px",
    height: "80px",
    borderRadius: "50%",
    backgroundColor: "#2a2a2a",
    border: "3px solid #3a3a3a",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "32px",
    fontWeight: 700,
    color: "#fff",
    marginBottom: "16px"
  },
  podiumAvatarFirst: {
    width: "96px",
    height: "96px",
    fontSize: "40px",
    borderColor: "#ffd700",
    backgroundColor: "rgba(255, 215, 0, 0.1)"
  },
  podiumUsername: {
    margin: "0 0 16px 0",
    fontSize: "18px",
    fontWeight: 600,
    color: "#fff"
  },
  podiumStats: {
    display: "flex",
    gap: "16px",
    width: "100%",
    justifyContent: "space-between"
  },
  podiumStat: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "4px"
  },
  podiumStatLabel: {
    fontSize: "11px",
    color: "#666",
    textTransform: "uppercase",
    fontWeight: 600
  },
  podiumStatValue: {
    fontSize: "14px",
    color: "#ccc",
    fontWeight: 600
  },
  leaderboardList: {
    maxWidth: "800px",
    margin: "0 auto"
  },
  leaderboardItem: {
    display: "flex",
    alignItems: "center",
    gap: "20px",
    padding: "24px 32px",
    background: "linear-gradient(135deg, rgba(20, 20, 35, 0.9) 0%, rgba(15, 15, 25, 0.9) 100%)",
    border: "1px solid rgba(0, 216, 255, 0.15)",
    borderRadius: "16px",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)",
    transition: "all 0.3s ease",
    cursor: "pointer",
    marginBottom: "10px"
  },
  leaderboardRank: {
    fontSize: "18px",
    fontWeight: 700,
    color: "#666",
    width: "40px"
  },
  leaderboardAvatar: {
    width: "48px",
    height: "48px",
    borderRadius: "50%",
    backgroundColor: "#2a2a2a",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "20px",
    fontWeight: 600,
    color: "#fff"
  },
  leaderboardInfo: {
    flex: 1
  },
  leaderboardUsername: {
    margin: "0 0 4px 0",
    fontSize: "16px",
    fontWeight: 600,
    color: "#fff"
  },
  leaderboardMeta: {
    fontSize: "13px",
    color: "#666"
  },
  leaderboardStats: {
    display: "flex",
    gap: "24px"
  },
  leaderboardStatItem: {
    display: "flex",
    alignItems: "center",
    gap: "6px"
  },
  leaderboardStatIcon: {
    fontSize: "12px",
    color: "#666"
  },
  leaderboardStatValue: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#999"
  },

  leaderboardSubTabs: {
    display: "flex",
    gap: "12px",
    marginBottom: "20px",
    justifyContent: "center"
  },
  subTabBtn: {
    padding: "12px 32px",
    background: "rgba(255, 255, 255, 0.03)",
    color: "#888",
    border: "1px solid rgba(255, 255, 255, 0.05)",
    borderRadius: "12px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)",
    fontFamily: "inherit",
    letterSpacing: "0.5px"
  },
  subTabBtnActive: {
    background: "linear-gradient(135deg, #00d8ff 0%, #c084fc 100%)",
    color: "#fff",
    border: "1px solid transparent",
    boxShadow: "0 4px 20px rgba(0, 216, 255, 0.25)"
  },
  topTracksList: {
    maxWidth: "1400px",
    margin: "0 auto"
  },
  trackRank: {
    fontSize: "18px",
    fontWeight: 700,
    color: "#666",
    width: "50px",
    textAlign: "center",
    flexShrink: 0
  }
};

const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

export default Explorer;